import { relations, InferSelectModel } from 'drizzle-orm';
import {
  boolean,
  customType,
  doublePrecision,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
  json,
  real,
  foreignKey,
  pgSchema,
  date
} from 'drizzle-orm/pg-core';


import type { AdapterAccountType } from 'next-auth/adapters';



// Custom Types
export const bytea = customType<{
  data: Buffer | null;
  notNull: false;
  default: false;
}>({
  dataType() {
    return 'bytea';
  },
  toDriver(val: Buffer | null) {
    return val;
  },
  fromDriver(value: unknown) {
    if (value === null) {
      return null;
    }

    if (value instanceof Buffer) {
      return value;
    }

    if (typeof value === 'string') {
      return Buffer.from(value, 'hex');
    }

    throw new Error(`Unexpected type received from driver: ${typeof value}`);
  }
});

// Enums
export enum ActionType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete'
}

export enum ActorType {
  SYSTEM = 'system',
  MEMBER = 'member',
  API = 'api'
}

export enum ContactRecord {
  PERSON = 'person',
  COMPANY = 'company'
}

export enum ContactStage {
  LEAD = 'lead',
  QUALIFIED = 'qualified',
  OPPORTUNITY = 'opportunity',
  PROPOSAL = 'proposal',
  IN_NEGOTIATION = 'inNegotiation',
  LOST = 'lost',
  WON = 'won'
}

export enum ContactTaskStatus {
  OPEN = 'open',
  COMPLETED = 'completed'
}

export enum DayOfWeek {
  SUNDAY = 'sunday',
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday'
}

export enum FeedbackCategory {
  SUGGESTION = 'suggestion',
  PROBLEM = 'problem',
  QUESTION = 'question'
}

export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REVOKED = 'revoked'
}

export enum Role {
  MEMBER = 'member',
  ADMIN = 'admin'
}

export enum WebhookTrigger {
  CONTACT_CREATED = 'contactCreated',
  CONTACT_UPDATED = 'contactUpdated',
  CONTACT_DELETED = 'contactDeleted'
}

// Usage metering — paid external transactions recorded in the usageEvent ledger.
export enum UsageProvider {
  ANTHROPIC = 'anthropic',
  OPENAI = 'openai',
  XAI = 'xai',
  FIRECRAWL = 'firecrawl',
  HUNTER = 'hunter',
  PDL = 'pdl',
  APOLLO = 'apollo',
  GOOGLE_PLACES = 'google_places',
  GOOGLE_SEARCH = 'google_search'
}

// Logical billable unit a usage event consumes from an org's plan allowance.
export enum UsageMeter {
  ENRICHMENT = 'enrichment',
  DOSSIER = 'dossier',
  DISCOVERY = 'discovery',
  AI_CHAT = 'ai_chat',
  WEB_SEARCH = 'web_search'
}

// Who triggered the usage: an interactive user, an API key (MCP), or the system (Celery).
export enum UsageActorType {
  USER = 'user',
  API = 'api',
  SYSTEM = 'system'
}

function enumToPgEnum<T extends Record<string, string>>(myEnum: T) {
  return Object.values(myEnum).map((value) => `${value}`) as [
    T[keyof T],
    ...T[keyof T][]
  ];
}

// use lowercase: https://github.com/drizzle-team/drizzle-orm/issues/1564#issuecomment-2320605690

export const actionTypeEnum = pgEnum('actiontype', enumToPgEnum(ActionType));
export const actorTypeEnum = pgEnum('actortype', enumToPgEnum(ActorType));
export const contactRecordEnum = pgEnum(
  'contactrecord',
  enumToPgEnum(ContactRecord)
);
export const contactStageEnum = pgEnum(
  'contactstage',
  enumToPgEnum(ContactStage)
);
export const contactTaskStatusEnum = pgEnum(
  'contacttaskstatus',
  enumToPgEnum(ContactTaskStatus)
);
export const dayOfWeekEnum = pgEnum('dayofweek', enumToPgEnum(DayOfWeek));
export const feedbackCategoryEnum = pgEnum(
  'feedbackcategory',
  enumToPgEnum(FeedbackCategory)
);
export const invitationStatusEnum = pgEnum(
  'invitationstatus',
  enumToPgEnum(InvitationStatus)
);
export const roleEnum = pgEnum('Role', enumToPgEnum(Role));
export const webhookTriggerEnum = pgEnum(
  'webhooktrigger',
  enumToPgEnum(WebhookTrigger)
);
export const usageProviderEnum = pgEnum(
  'usageprovider',
  enumToPgEnum(UsageProvider)
);
export const usageMeterEnum = pgEnum('usagemeter', enumToPgEnum(UsageMeter));
export const usageActorTypeEnum = pgEnum(
  'usageactortype',
  enumToPgEnum(UsageActorType)
);

// Tables

export const apiKeyTable = pgTable(
  'apiKey',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    description: varchar('description', { length: 70 }).notNull(),
    hashedKey: text('hashedKey').notNull(),
    expiresAt: timestamp('expiresAt', { precision: 3, mode: 'date' }),
    lastUsedAt: timestamp('lastUsedAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_apiKey_hashedKey_unique').using(
      'btree',
      table.hashedKey.asc().nullsLast().op('text_ops')
    ),
    index('IX_apiKey_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const authenticatorAppTable = pgTable(
  'authenticatorApp',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    accountName: varchar('accountName', { length: 255 }).notNull(),
    issuer: varchar('issuer', { length: 255 }).notNull(),
    secret: varchar('secret', { length: 255 }).notNull(),
    recoveryCodes: varchar('recoveryCodes', { length: 1024 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_authenticatorApp_userId_unique').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const changeEmailRequestTable = pgTable(
  'changeEmailRequest',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    email: text('email').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull(),
    valid: boolean('valid').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_changeEmailRequest_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactTable = pgTable(
  'contact',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    name: varchar('name', { length: 255 }).notNull(),
    email: varchar('email', { length: 255 }),
    address: varchar('address', { length: 255 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date()),
    image: varchar('image', { length: 2048 }),
    stage: contactStageEnum('stage').default(ContactStage.LEAD).notNull(),
    phone: varchar('phone', { length: 32 }),
    record: contactRecordEnum('record').default(ContactRecord.PERSON).notNull(),
    canonicalDomain: varchar('canonical_domain', { length: 255 }),

    companiesHouseNumber: text('ch_number'),
    companySicCode: text('company_sic'),
    companyIncDate: timestamp('company_inc_date'),

    websiteUrl: text('website_url'),      // To store the specific URL (e.g. deep link)
    aiIcebreaker: text('ai_icebreaker'),  // Keep the icebreaker for future reference

    // --- 🆕 Google-Specific Data (Migrating now) ---
    gmapsPlaceId: varchar('gmaps_place_id', { length: 255 }),
    rawGmapsData: jsonb('raw_gmaps_data'),
    rating: real('rating'),
    latitude: real('latitude'),
    longitude: real('longitude'),
  },
  (table) => [
    index('IX_contact_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    // Index to allow for fast searching of domains within an organization.
    index('IX_contact_canonical_domain').on(table.organizationId, table.canonicalDomain),
    uniqueIndex('IX_contact_org_gmaps_place_id_unique').on(table.organizationId, table.gmapsPlaceId),
  ]
);

export const contactCommentTable = pgTable(
  'contactComment',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    text: varchar('text', { length: 2000 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_contactComment_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_contactComment_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactNoteTable = pgTable(
  'contactNote',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    text: varchar('text', { length: 8000 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_contactNote_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_contactNote_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactPageVisitTable = pgTable(
  'contactPageVisit',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    timestamp: timestamp('timestamp', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    })
  },
  (table) => [
    index('IX_contactPageVisit_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_contactPageVisit_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactImageTable = pgTable(
  'contactImage',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_contactImage_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactTaskTable = pgTable(
  'contactTask',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    title: varchar('title', { length: 255 }).notNull(),
    description: varchar('description', { length: 8000 }),
    status: contactTaskStatusEnum('status')
      .default(ContactTaskStatus.OPEN)
      .notNull(),
    dueDate: timestamp('dueDate', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_contactTask_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const favoriteTable = pgTable(
  'favorite',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    order: integer('order').default(0).notNull()
  },
  (table) => [
    index('IX_favorite_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_favorite_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const invitationTable = pgTable(
  'invitation',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    token: uuid('token').notNull().defaultRandom(),
    email: varchar('email', { length: 255 }).notNull(),
    role: roleEnum('role').default(Role.MEMBER).notNull(),
    status: invitationStatusEnum('status')
      .default(InvitationStatus.PENDING)
      .notNull(),
    lastSentAt: timestamp('lastSentAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_invitation_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_invitation_token').using(
      'btree',
      table.token.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactTagTable = pgTable(
  'contactTag',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    text: varchar('text', { length: 128 }).notNull()
  },
  (table) => [
    uniqueIndex('IX_contactTag_text_unique').using(
      'btree',
      table.text.asc().nullsLast().op('text_ops')
    )
  ]
);

export const accountTable = pgTable(
  'account',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade' }),
    type: text('type').$type<AdapterAccountType>().notNull(),
    provider: text('provider').notNull(),
    providerAccountId: text('providerAccountId').notNull(),
    refresh_token: text('refresh_token'),
    access_token: text('access_token'),
    expires_at: integer('expires_at'),
    token_type: text('token_type'),
    scope: text('scope'),
    id_token: text('id_token'),
    session_state: text('session_state'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_account_provider_providerAccountId_unique').using(
      'btree',
      table.provider.asc().nullsLast().op('text_ops'),
      table.providerAccountId.asc().nullsLast().op('text_ops')
    ),
    index('IX_account_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const feedbackTable = pgTable(
  'feedback',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    category: feedbackCategoryEnum('category')
      .default(FeedbackCategory.SUGGESTION)
      .notNull(),
    message: varchar('message', { length: 4000 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_feedback_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_feedback_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const notificationTable = pgTable(
  'notification',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    subject: varchar('subject', { length: 128 }),
    content: varchar('content', { length: 8000 }).notNull(),
    link: varchar('link', { length: 2000 }),
    seenAt: timestamp('seenAt', { precision: 3, mode: 'date' }),
    dismissed: boolean('dismissed').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_notification_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const resetPasswordRequestTable = pgTable(
  'resetPasswordRequest',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    email: text('email').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_resetPasswordRequest_email').using(
      'btree',
      table.email.asc().nullsLast().op('text_ops')
    )
  ]
);

export const userImageTable = pgTable(
  'userImage',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_userImage_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const userTable = pgTable(
  'user',
  {
    id: uuid('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    email: text('email').unique(),
    emailVerified: timestamp('emailVerified', { precision: 3, mode: 'date' }),
    password: varchar('password', { length: 60 }),
    lastLogin: timestamp('lastLogin', { precision: 3, mode: 'date' }),
    locale: varchar('locale', { length: 8 }).default('en-US').notNull(),
    completedOnboarding: boolean('completedOnboarding')
      .default(false)
      .notNull(),
    enabledContactsNotifications: boolean('enabledContactsNotifications')
      .default(false)
      .notNull(),
    enabledInboxNotifications: boolean('enabledInboxNotifications')
      .default(false)
      .notNull(),
    enabledNewsletter: boolean('enabledNewsletter').default(false).notNull(),
    enabledProductUpdates: boolean('enabledProductUpdates')
      .default(false)
      .notNull(),
    enabledWeeklySummary: boolean('enabledWeeklySummary')
      .default(false)
      .notNull(),
    image: varchar('image', { length: 2048 }),
    name: varchar('name', { length: 64 }).notNull(),
    phone: varchar('phone', { length: 32 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_user_email_unique').using(
      'btree',
      table.email.asc().nullsLast().op('text_ops')
    ),
    index('IX_user_name').using(
      'btree',
      table.name.asc().nullsLast().op('text_ops')
    )
  ]
);

export const sessionTable = pgTable(
  'session',
  {
    sessionToken: text('sessionToken').primaryKey(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade' }),
    expires: timestamp('expires', { mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_session_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const verificationTokenTable = pgTable(
  'verificationToken',
  {
    identifier: text('identifier').notNull(),
    token: text('token').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull()
  },
  (table) => [
    uniqueIndex('IX_verificationToken_identifier_unique').using(
      'btree',
      table.identifier.asc().nullsLast().op('text_ops'),
      table.token.asc().nullsLast().op('text_ops')
    )
  ]
);

export const workHoursTable = pgTable(
  'workHours',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    dayOfWeek: dayOfWeekEnum('dayOfWeek').default(DayOfWeek.SUNDAY).notNull()
  },
  (table) => [
    index('IX_workHours_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const workTimeSlotTable = pgTable(
  'workTimeSlot',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    workHoursId: uuid('workHoursId')
      .notNull()
      .references(() => workHoursTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    start: timestamp('start', { precision: 2, withTimezone: false }).notNull(),
    end: timestamp('end', { precision: 2, withTimezone: false }).notNull()
  },
  (table) => [
    index('IX_workTimeSlot_workHoursId').using(
      'btree',
      table.workHoursId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const contactActivityTable = pgTable(
  'contactActivity',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    actionType: actionTypeEnum('actionType').notNull(),
    actorId: varchar('actorId', { length: 255 }).notNull(),
    actorType: actorTypeEnum('actorType').notNull(),
    metadata: jsonb('metadata'),
    occurredAt: timestamp('occurredAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    index('IX_contactActivity_contactId').using(
      'btree',
      table.contactId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_contactActivity_occurredAt').using(
      'btree',
      table.occurredAt.asc().nullsLast().op('timestamp_ops')
    )
  ]
);

export const webhookTable = pgTable(
  'webhook',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    secret: varchar('secret', { length: 1024 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    triggers: webhookTriggerEnum('triggers').array().notNull(),
    url: varchar('url', { length: 2000 }).notNull()
  },
  (table) => [
    {
      organizationIdIndex: index('IX_webhook_organizationId').on(
        table.organizationId
      )
    }
  ]
);

export const membershipTable = pgTable(
  'membership',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    role: roleEnum('role').default(Role.MEMBER).notNull(),
    isOwner: boolean('isOwner').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    uniqueIndex('IX_membership_organizationId_userId_unique').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops'),
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_membership_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_membership_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const organizationLogoTable = pgTable(
  'organizationLogo',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_organizationLogo_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const organizationTable = pgTable(
  'organization',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    name: varchar('name', { length: 255 }).notNull(),
    address: varchar('address', { length: 255 }),
    email: varchar('email', { length: 255 }),
    website: varchar('website', { length: 2000 }),
    phone: varchar('phone', { length: 32 }),
    facebookPage: varchar('facebookPage', { length: 2000 }),
    instagramProfile: varchar('instagramProfile', { length: 2000 }),
    linkedInProfile: varchar('linkedInProfile', { length: 2000 }),
    tikTokProfile: varchar('tikTokProfile', { length: 2000 }),
    xProfile: varchar('xProfile', { length: 2000 }),
    youTubeChannel: varchar('youTubeChannel', { length: 2000 }),
    logo: varchar('logo', { length: 2048 }),
    slug: varchar('slug', { length: 255 }).notNull(),
    billingCustomerId: varchar('billingCustomerId', { length: 255 }),
    billingEmail: varchar('billingEmail', { length: 255 }),
    billingLine1: varchar('billingLine1', { length: 255 }),
    billingLine2: varchar('billingLine2', { length: 255 }),
    billingCountry: varchar('billingCountry', { length: 3 }),
    billingPostalCode: varchar('billingPostalCode', { length: 16 }),
    billingCity: varchar('billingCity', { length: 255 }),
    billingState: varchar('billingState', { length: 255 })
  },
  (table) => [
    index('IX_organization_billingCustomerId').using(
      'btree',
      table.billingCustomerId.asc().nullsLast().op('text_ops')
    ),
    uniqueIndex('IX_organization_slug_unique').using(
      'btree',
      table.slug.asc().nullsLast().op('text_ops')
    )
  ]
);

export const subscriptionTable = pgTable('subscription', {
  id: text('id').primaryKey(),
  organizationId: uuid('organizationId').notNull(),
  status: varchar('status', { length: 64 }).notNull(),
  active: boolean('active').notNull().default(false),
  provider: varchar('provider', { length: 32 }).notNull(),
  cancelAtPeriodEnd: boolean('cancelAtPeriodEnd').notNull().default(false),
  currency: varchar('currency', { length: 3 }).notNull(),
  periodStartsAt: timestamp('periodStartsAt', { withTimezone: true, precision: 6 }).notNull(),
  periodEndsAt: timestamp('periodEndsAt', { withTimezone: true, precision: 6 }).notNull(),
  trialStartsAt: timestamp('trialStartsAt', { withTimezone: true, precision: 6 }),
  trialEndsAt: timestamp('trialEndsAt', { withTimezone: true, precision: 6 }),
  createdAt: timestamp('createdAt', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updatedAt', { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index('IX_subscription_organizationId').using(
    'btree',
    table.organizationId.asc().nullsLast().op('uuid_ops')
  )
]);

export const subscriptionItemTable = pgTable('subscriptionItem', {
  id: text('id').primaryKey(),
  subscriptionId: text('subscriptionId').notNull(),
  quantity: integer('quantity').notNull(),
  productId: text('productId').notNull(),
  variantId: text('variantId').notNull(),
  priceAmount: doublePrecision('priceAmount'),
  interval: text('interval').notNull(),
  intervalCount: integer('intervalCount').notNull(),
  type: text('type'),
  model: text('model'),
}, (table) => [
  index('IX_subscriptionItem_subscriptionId').using(
    'btree',
    table.subscriptionId.asc().nullsLast()
  )
]);

export const orderTable = pgTable('order', {
  id: text('id').primaryKey(),
  organizationId: uuid('organizationId').notNull(),
  status: varchar('status', { length: 64 }).notNull(),
  provider: varchar('provider', { length: 32 }).notNull(),
  totalAmount: doublePrecision('totalAmount').notNull(),
  currency: varchar('currency', { length: 3 }).notNull(),
  createdAt: timestamp('createdAt', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updatedAt', { withTimezone: true }).notNull().defaultNow(),
},
  (table) => [
    index('IX_order_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]);

export const orderItemTable = pgTable('orderItem', {
  id: text('id').primaryKey(),
  orderId: text('orderId').notNull(),
  quantity: integer('quantity').notNull(),
  productId: text('productId').notNull(),
  variantId: text('variantId').notNull(),
  priceAmount: doublePrecision('priceAmount'),
  type: text('type'),
  model: text('model'),
},
  (table) => [
    index('IX_orderItem_orderId').using(
      'btree',
      table.orderId.asc().nullsLast()
    )
  ]);

export const contactToContactTagTable = pgTable(
  'contactToContactTag',
  {
    contactId: uuid('contactId')
      .notNull()
      .references(() => contactTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    contactTagId: uuid('contactTagId')
      .notNull()
      .references(() => contactTagTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      })
  },
  (table) => [
    {
      pk: primaryKey({
        columns: [table.contactId, table.contactTagId]
      }),
      contactIdIdx: index('IX_contactToContactTag_contactId').on(
        table.contactId
      ),
      contactTagIdIdx: index('IX_contactToContactTag_contactTagId').on(
        table.contactTagId
      )
    }
  ]
);

// Relations
export const apiKeyRelations = relations(apiKeyTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [apiKeyTable.organizationId],
    references: [organizationTable.id],
  }),
}));


export const organizationRelations = relations(
  organizationTable,
  ({ many }) => ({
    apiKeys: many(apiKeyTable),
    contacts: many(contactTable),
    invitations: many(invitationTable),
    feedbacks: many(feedbackTable),
    workHours: many(workHoursTable),
    webhooks: many(webhookTable),
    memberships: many(membershipTable),
    chats: many(chat),
    documents: many(document),
    analysisProfiles: many(analysisProfileTable),
    usageEvents: many(usageEventTable),
  })
);

export const authenticatorAppRelations = relations(
  authenticatorAppTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [authenticatorAppTable.userId],
      references: [userTable.id]
    })
  })
);

export const userRelations = relations(userTable, ({ many }) => ({
  authenticatorApps: many(authenticatorAppTable),
  changeEmailRequests: many(changeEmailRequestTable),
  contactComments: many(contactCommentTable),
  contactNotes: many(contactNoteTable),
  contactPageVisits: many(contactPageVisitTable),
  favorites: many(favoriteTable),
  accounts: many(accountTable),
  feedbacks: many(feedbackTable),
  notifications: many(notificationTable),
  sessions: many(sessionTable),
  memberships: many(membershipTable),

  chats: many(chat),
  documents: many(document),
  suggestions: many(suggestion),
  analysisProfiles: many(analysisProfileTable),
}));

export const changeEmailRequestRelations = relations(
  changeEmailRequestTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [changeEmailRequestTable.userId],
      references: [userTable.id]
    })
  })
);

export const contactRelations = relations(contactTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [contactTable.organizationId],
    references: [organizationTable.id],
  }),
  contactComments: many(contactCommentTable),
  contactNotes: many(contactNoteTable),
  contactPageVisits: many(contactPageVisitTable),
  contactTasks: many(contactTaskTable),
  favorites: many(favoriteTable),
  contactActivities: many(contactActivityTable),
  contactToContactTags: many(contactToContactTagTable),
  sources: many(prospectSourceTable),
  generatedEmails: many(generatedEmailsTable),
}));

export const contactCommentRelations = relations(
  contactCommentTable,
  ({ one }) => ({
    contact: one(contactTable, {
      fields: [contactCommentTable.contactId],
      references: [contactTable.id]
    }),
    user: one(userTable, {
      fields: [contactCommentTable.userId],
      references: [userTable.id]
    })
  })
);

export const contactNoteRelations = relations(contactNoteTable, ({ one }) => ({
  contact: one(contactTable, {
    fields: [contactNoteTable.contactId],
    references: [contactTable.id]
  }),
  user: one(userTable, {
    fields: [contactNoteTable.userId],
    references: [userTable.id]
  })
}));

export const contactPageVisitRelations = relations(
  contactPageVisitTable,
  ({ one }) => ({
    contact: one(contactTable, {
      fields: [contactPageVisitTable.contactId],
      references: [contactTable.id]
    }),
    user: one(userTable, {
      fields: [contactPageVisitTable.userId],
      references: [userTable.id]
    })
  })
);

export const contactTaskRelations = relations(contactTaskTable, ({ one }) => ({
  contact: one(contactTable, {
    fields: [contactTaskTable.contactId],
    references: [contactTable.id]
  })
}));

export const favoriteRelations = relations(favoriteTable, ({ one }) => ({
  contact: one(contactTable, {
    fields: [favoriteTable.contactId],
    references: [contactTable.id]
  }),
  user: one(userTable, {
    fields: [favoriteTable.userId],
    references: [userTable.id]
  })
}));

export const invitationRelations = relations(invitationTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [invitationTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const accountRelations = relations(accountTable, ({ one }) => ({
  user: one(userTable, {
    fields: [accountTable.userId],
    references: [userTable.id]
  })
}));

export const feedbackRelations = relations(feedbackTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [feedbackTable.organizationId],
    references: [organizationTable.id]
  }),
  user: one(userTable, {
    fields: [feedbackTable.userId],
    references: [userTable.id]
  })
}));

export const notificationRelations = relations(
  notificationTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [notificationTable.userId],
      references: [userTable.id]
    })
  })
);

export const sessionRelations = relations(sessionTable, ({ one }) => ({
  user: one(userTable, {
    fields: [sessionTable.userId],
    references: [userTable.id]
  })
}));

export const workHoursRelations = relations(
  workHoursTable,
  ({ one, many }) => ({
    organization: one(organizationTable, {
      fields: [workHoursTable.organizationId],
      references: [organizationTable.id]
    }),
    workTimeSlots: many(workTimeSlotTable)
  })
);

export const workTimeSlotRelations = relations(
  workTimeSlotTable,
  ({ one }) => ({
    workHour: one(workHoursTable, {
      fields: [workTimeSlotTable.workHoursId],
      references: [workHoursTable.id]
    })
  })
);

export const contactActivityRelations = relations(
  contactActivityTable,
  ({ one }) => ({
    contact: one(contactTable, {
      fields: [contactActivityTable.contactId],
      references: [contactTable.id]
    })
  })
);

export const webhookRelations = relations(webhookTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [webhookTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const membershipRelations = relations(membershipTable, ({ one }) => ({
  user: one(userTable, {
    fields: [membershipTable.userId],
    references: [userTable.id]
  }),
  organization: one(organizationTable, {
    fields: [membershipTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const organizationLogoRelations = relations(
  organizationLogoTable,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [organizationLogoTable.organizationId],
      references: [organizationTable.id]
    })
  })
);

export const subscriptionRelations = relations(subscriptionTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [subscriptionTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const subscriptionItemRelations = relations(subscriptionItemTable, ({ one }) => ({
  organization: one(subscriptionTable, {
    fields: [subscriptionItemTable.subscriptionId],
    references: [subscriptionTable.id]
  })
}));

export const orderRelations = relations(orderTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [orderTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const orderItemRelations = relations(orderItemTable, ({ one }) => ({
  organization: one(orderTable, {
    fields: [orderItemTable.orderId],
    references: [orderTable.id]
  })
}));

export const contactTagRelations = relations(contactTagTable, ({ many }) => ({
  contactToContactTags: many(contactToContactTagTable)
}));

export const contactToContactTagRelations = relations(
  contactToContactTagTable,
  ({ one }) => ({
    contact: one(contactTable, {
      fields: [contactToContactTagTable.contactId],
      references: [contactTable.id]
    }),
    contactTag: one(contactTagTable, {
      fields: [contactToContactTagTable.contactTagId],
      references: [contactTagTable.id]
    })
  })
);


//  from Vercel Chat - Schemas for Chat, Message, Document etc.
// MODIFIED: chat and document tables below are updated

export type User = InferSelectModel<typeof userTable>;

// MODIFIED: Added organizationId and indexes
export const chat = pgTable('Chat', {
  id: uuid('id').primaryKey().notNull().defaultRandom(),
  createdAt: timestamp('createdAt').notNull(),
  title: text('title').notNull(),
  userId: uuid('userId')
    .notNull()
    .references(() => userTable.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
  visibility: varchar('visibility', { enum: ['public', 'private'] })
    .notNull()
    .default('private'),
  organizationId: uuid('organizationId') // NEW: Optional link to organization
    .references(() => organizationTable.id, { onDelete: 'set null', onUpdate: 'cascade' }),

  analysisProfileId: uuid('analysisProfileId')
    .references(() => analysisProfileTable.id, { onDelete: 'set null', onUpdate: 'cascade' }),
}, (table) => ({
  userIdx: index('IX_Chat_userId').using('btree', table.userId.asc().nullsLast().op('uuid_ops')), // NEW: Index for userId
  organizationIdx: index('IX_Chat_organizationId').using('btree', table.organizationId.asc().nullsLast().op('uuid_ops')), // NEW: Index for organizationId
  analysisProfileIdx: index('IX_Chat_analysisProfileId').using('btree', table.analysisProfileId.asc().nullsLast().op('uuid_ops')),
}));

export type Chat = InferSelectModel<typeof chat>;

export const messageDeprecated = pgTable('Message', {
  id: uuid('id').primaryKey().notNull().defaultRandom(),
  chatId: uuid('chatId')
    .notNull()
    .references(() => chat.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
  role: varchar('role').notNull(),
  content: json('content').notNull(),
  createdAt: timestamp('createdAt').notNull(),
});

export type MessageDeprecated = InferSelectModel<typeof messageDeprecated>;

export const message = pgTable('Message_v2', {
  id: uuid('id').primaryKey().notNull().defaultRandom(),
  chatId: uuid('chatId')
    .notNull()
    .references(() => chat.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
  role: varchar('role').notNull(),
  parts: json('parts').notNull(),
  attachments: json('attachments').notNull(),
  createdAt: timestamp('createdAt').notNull(),
});

export type DBMessage = InferSelectModel<typeof message>;

export const voteDeprecated = pgTable(
  'Vote',
  {
    chatId: uuid('chatId')
      .notNull()
      .references(() => chat.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
    messageId: uuid('messageId')
      .notNull()
      .references(() => messageDeprecated.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
    isUpvoted: boolean('isUpvoted').notNull(),
  },
  (table) => {
    return {
      pk: primaryKey({ columns: [table.chatId, table.messageId] }),
    };
  },
);

export type VoteDeprecated = InferSelectModel<typeof voteDeprecated>;

export const vote = pgTable(
  'Vote_v2',
  {
    chatId: uuid('chatId')
      .notNull()
      .references(() => chat.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
    messageId: uuid('messageId')
      .notNull()
      .references(() => message.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
    isUpvoted: boolean('isUpvoted').notNull(),
  },
  (table) => {
    return {
      pk: primaryKey({ columns: [table.chatId, table.messageId] }),
    };
  },
);

export type Vote = InferSelectModel<typeof vote>;

// MODIFIED: Added organizationId and indexes
export const document = pgTable(
  'Document',
  {
    id: uuid('id').notNull().defaultRandom(),
    createdAt: timestamp('createdAt').notNull(),
    title: text('title').notNull(),
    content: text('content'),
    kind: varchar('kind', { enum: ['text', 'code', 'image', 'sheet'] })
      .notNull()
      .default('text'),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
    organizationId: uuid('organizationId') // NEW: Optional link to organization
      .references(() => organizationTable.id, { onDelete: 'set null', onUpdate: 'cascade' }),
  },
  (table) => {
    return {
      pk: primaryKey({ columns: [table.id, table.createdAt] }),
      userIdx: index('IX_Document_userId').using('btree', table.userId.asc().nullsLast().op('uuid_ops')), // NEW: Index for userId
      organizationIdx: index('IX_Document_organizationId').using('btree', table.organizationId.asc().nullsLast().op('uuid_ops')), // NEW: Index for organizationId
    };
  },
);

export type Document = InferSelectModel<typeof document>;

export const suggestion = pgTable(
  'Suggestion',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(), // MODIFIED: Added primaryKey() here as it was missing before pk object
    documentId: uuid('documentId').notNull(),
    documentCreatedAt: timestamp('documentCreatedAt').notNull(),
    originalText: text('originalText').notNull(),
    suggestedText: text('suggestedText').notNull(),
    description: text('description'),
    isResolved: boolean('isResolved').notNull().default(false),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade', onUpdate: 'cascade' }), // Added onDelete/onUpdate for clarity
    createdAt: timestamp('createdAt').notNull(),
  },
  (table) => ({
    // pk: primaryKey({ columns: [table.id] }), // This is now redundant if id is primaryKey()
    documentRef: foreignKey({
      columns: [table.documentId, table.documentCreatedAt],
      foreignColumns: [document.id, document.createdAt],
      name: 'FK_suggestion_document' // NEW: Optional FK name
    }).onDelete('cascade').onUpdate('cascade'), // Added onDelete/onUpdate for clarity
    userIdx: index('IX_Suggestion_userId').using('btree', table.userId.asc().nullsLast().op('uuid_ops')), // NEW: Index for userId
    documentIdx: index('IX_Suggestion_document').using('btree', table.documentId.asc().nullsLast().op('uuid_ops'), table.documentCreatedAt.asc().nullsLast()) // NEW: Index for document composite FK
  }),
);

export type Suggestion = InferSelectModel<typeof suggestion>;

export const stream = pgTable(
  'Stream',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(), // MODIFIED: Added primaryKey() here
    chatId: uuid('chatId').notNull(),
    createdAt: timestamp('createdAt').notNull(),
  },
  (table) => ({
    // pk: primaryKey({ columns: [table.id] }), // Redundant
    chatRef: foreignKey({
      columns: [table.chatId],
      foreignColumns: [chat.id],
      name: 'FK_stream_chat' // NEW: Optional FK name
    }).onDelete('cascade').onUpdate('cascade'), // Added onDelete/onUpdate for clarity
    chatIdIdx: index('IX_Stream_chatId').using('btree', table.chatId.asc().nullsLast().op('uuid_ops')) // NEW: Index for chatId
  }),
);

export type Stream = InferSelectModel<typeof stream>;


export const chatRelations = relations(chat, ({ one, many }) => ({
  user: one(userTable, {
    fields: [chat.userId],
    references: [userTable.id]
  }),
  organization: one(organizationTable, {
    fields: [chat.organizationId],
    references: [organizationTable.id]
  }),
  analysisProfile: one(analysisProfileTable, {
    fields: [chat.analysisProfileId],
    references: [analysisProfileTable.id]
  }),
  messages: many(message),
  votes: many(vote)
}));

export const messageRelations = relations(message, ({ one, many }) => ({
  chat: one(chat, {
    fields: [message.chatId],
    references: [chat.id]
  }),
  votes: many(vote)
}));

export const voteRelations = relations(vote, ({ one }) => ({
  chat: one(chat, {
    fields: [vote.chatId],
    references: [chat.id]
  }),
  message: one(message, {
    fields: [vote.messageId],
    references: [message.id]
  })
}));

export const documentRelations = relations(document, ({ one, many }) => ({
  user: one(userTable, {
    fields: [document.userId],
    references: [userTable.id]
  }),
  organization: one(organizationTable, {
    fields: [document.organizationId],
    references: [organizationTable.id]
  }),
  suggestions: many(suggestion)
}));

export const suggestionRelations = relations(suggestion, ({ one }) => ({
  document: one(document, {
    // Both fields must be included here
    fields: [suggestion.documentId, suggestion.documentCreatedAt],
    references: [document.id, document.createdAt] // And both here
  }),
  user: one(userTable, {
    fields: [suggestion.userId],
    references: [userTable.id]
  })
}));

export const streamRelations = relations(stream, ({ one }) => ({
  chat: one(chat, {
    fields: [stream.chatId],
    references: [chat.id],
  }),
}));


// 1. The main table for a profiling job
export const analysisProfileTable = pgTable('analysisProfile', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  userId: uuid('userId').notNull().references(() => userTable.id, { onDelete: 'cascade' }),
  status: varchar('status', { length: 20 }).notNull().default('pending'), // 'pending', 'in_progress', 'completed', 'failed'
  targetUrl: text('targetUrl').notNull(),

  // This will store the 'ai_analysis' object from your JSON
  aiAnalysis: jsonb('aiAnalysis').$type<{
    company_name: string;
    core_services: string[];
    target_audience: string;
    tone_of_voice: string;
    value_proposition: string;
  }>(),

  // A flexible JSONB field for storing all prospecting parameters like
  // location, business types, exclusion keywords, etc.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: jsonb('metadata').$type<{ [key: string]: any } | null>(),

  createdAt: timestamp('createdAt').defaultNow().notNull(),
  completedAt: timestamp('completedAt'),

  companiesHouseNumber: text('ch_number'),
});

// Business Summaries Table
export const businessSummaryTable = pgTable('businessSummary', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  businessName: varchar('businessName', { length: 255 }).notNull(),
  url: text('url'),
  industry: varchar('industry', { length: 255 }),
  aiSummary: text('aiSummary'),
  keyInsights: jsonb('keyInsights').$type<string[] | null>(),
  recommendations: jsonb('recommendations').$type<string[] | null>(),
  status: varchar('status', { length: 20 }).notNull().default('draft'), // 'draft', 'completed', 'pending', 'failed'
  createdAt: timestamp('createdAt').defaultNow().notNull(),
  updatedAt: timestamp('updatedAt').defaultNow().notNull(),
});

// 2. A table to store each scraped page's content
export const scrapedPageTable = pgTable('scrapedPage', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  url: text('url').notNull(),
  textContent: text('textContent'),
  textLength: integer('textLength'),

  createdAt: timestamp('createdAt').defaultNow().notNull(),
});

// 3. A table for found social media links
export const foundSocialLinkTable = pgTable('foundSocialLink', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  url: text('url').notNull(),
});

// --- Define the relationships ---
export const analysisProfileRelations = relations(analysisProfileTable, ({ one, many }) => ({
  // Belongs-to relationships
  user: one(userTable, {
    fields: [analysisProfileTable.userId],
    references: [userTable.id],
  }),
  organization: one(organizationTable, {
    fields: [analysisProfileTable.organizationId],
    references: [organizationTable.id],
  }),

  // Has-many relationships (all modules are now 'many')
  scrapedPages: many(scrapedPageTable),
  foundSocialLinks: many(foundSocialLinkTable),
  prospects: many(prospectsTable),
  generatedEmails: many(generatedEmailsTable),
  blogPlans: many(blogPlansTable),
  socialMediaPlans: many(socialMediaPlansTable),
  seoAuditResults: many(seoAuditResultsTable),
  websiteCopyAudits: many(websiteCopyAuditsTable),
  gmbPlans: many(gmbPlansTable),

  // DataForSEO integration
  serpRankings: many(serpRankingsTable),
  keywordResearch: many(keywordResearchTable),
  backlinkProfiles: many(backlinkProfileTable),
  onPageAudits: many(onPageAuditTable),
  competitiveAnalyses: many(competitiveAnalysisTable),
  gmbBusinessData: many(gmbBusinessDataTable),
  aiMentions: many(aiMentionsTable),
  domainAnalytics: many(domainAnalyticsTable),
  googleTrends: many(googleTrendsTable),

  chats: many(chat),
}));

export const scrapedPageRelations = relations(scrapedPageTable, ({ one }) => ({
  profile: one(analysisProfileTable, {
    fields: [scrapedPageTable.profileId],
    references: [analysisProfileTable.id],
  }),
}));

export const foundSocialLinkRelations = relations(foundSocialLinkTable, ({ one }) => ({
  profile: one(analysisProfileTable, {
    fields: [foundSocialLinkTable.profileId],
    references: [analysisProfileTable.id],
  }),
}));

export const businessSummaryRelations = relations(businessSummaryTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [businessSummaryTable.organizationId],
    references: [organizationTable.id],
  }),
}));


// --- Prospecting & Enrichment ---

export const prospectsTable = pgTable('prospect', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  // --- "Golden Record" Fields ---
  // These fields hold the best-known data, which can be enriched from multiple sources.
  name: text('name').notNull(),
  websiteUrl: text('website_url'),
  canonicalDomain: varchar('canonical_domain', { length: 255 }),
  vicinity: text('vicinity'),
  aiIcebreaker: text('ai_icebreaker'),

  // --- Google-Specific Enriched Data (retained as requested) ---
  gmapsPlaceId: varchar('gmaps_place_id', { length: 255 }),
  rawGmapsData: jsonb('raw_gmaps_data'),
  rating: real('rating'),
  latitude: real('latitude'),
  longitude: real('longitude'),

  companiesHouseNumber: text('ch_number'),
  contactEmail: text('contact_email'),

  types: text('types').array(),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  // This index is crucial for fast domain-based deduplication when a new source is added.
  index('IX_prospect_canonical_domain').on(table.organizationId, table.canonicalDomain),
  // This ensures a gmaps_place_id can only be linked to one prospect within an organization.
  uniqueIndex('IX_prospect_org_gmaps_place_id_unique').on(table.organizationId, table.gmapsPlaceId),
]));


// This is the "ledger" table that links a single prospect to multiple external data sources.
export const prospectSourceTable = pgTable('prospectSource', {
  id: uuid('id').primaryKey().defaultRandom(),
  prospectId: uuid('prospectId').references(() => prospectsTable.id, { onDelete: 'set null' }),
  contactId: uuid('contactId').references(() => contactTable.id, { onDelete: 'cascade' }),

  sourceName: varchar('source_name', { length: 50 }).notNull(), // e.g., 'google_maps', 'apollo_io'
  sourceId: text('source_id').notNull(),                        // The unique ID from that external service
  rawData: jsonb('raw_data'),                                   // The original, rich data from the source API

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  // This is the most important constraint. It prevents linking the same external record (e.g., apollo_id: 123) twice.
  uniqueIndex('IX_source_unique').on(table.sourceName, table.sourceId, table.prospectId, table.contactId),  // Index for quickly finding all sources for a given prospect
  index('IX_prospectSource_prospectId').on(table.prospectId),
]));


// --- Content Generation ---

export const generatedEmailsTable = pgTable('generatedEmail', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  prospectId: uuid('prospectId').references(() => prospectsTable.id, { onDelete: 'set null' }),
  contactId: uuid('contactId').references(() => contactTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  subject: text('subject').notNull(),
  body: text('body').notNull(),
  status: varchar('status', { length: 50 }), // e.g., 'success', 'error_no_icebreaker'

  feedbackRating: varchar('feedback_rating', { length: 10 }), // 'up' | 'down'
  userFeedback: text('user_feedback'),
  contentStatus: varchar('content_status', { length: 20 }).notNull().default('draft'), // 'draft' | 'planned' | 'sent' | 'archived'

  // Sending fields
  sentAt: timestamp('sent_at', { withTimezone: true }),
  sentVia: varchar('sent_via', { length: 50 }),           // 'resend' | 'smtp'
  enrollmentId: uuid('enrollmentId'),  // FK to emailSequenceEnrollmentTable — constraint lives in DB
  sequenceStep: integer('sequence_step'),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const blogPlansTable = pgTable('blogPlan', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  ideas: text('ideas').array(),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
});

export const generatedBlogPostsTable = pgTable('generatedBlogPost', {
  id: uuid('id').primaryKey().defaultRandom(),
  blogPlanId: uuid('blogPlanId').notNull().references(() => blogPlansTable.id, { onDelete: 'cascade' }),

  title: text('title').notNull(),
  content: text('content').notNull(),

  feedbackRating: varchar('feedback_rating', { length: 10 }), // 'up' | 'down'
  userFeedback: text('user_feedback'),
  contentStatus: varchar('content_status', { length: 20 }).notNull().default('draft'), // 'draft' | 'planned' | 'published' | 'archived'

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const socialMediaPlansTable = pgTable('socialMediaPlan', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
});

export const socialMediaPostsTable = pgTable('socialMediaPost', {
  id: uuid('id').primaryKey().defaultRandom(),
  planId: uuid('planId').notNull().references(() => socialMediaPlansTable.id, { onDelete: 'cascade' }),

  dayOfWeek: varchar('day_of_week', { length: 20 }),
  postIdea: text('post_idea'),
  postCopy: text('post_copy'),

  feedbackRating: varchar('feedback_rating', { length: 10 }), // 'up' | 'down'
  userFeedback: text('user_feedback'),
  contentStatus: varchar('content_status', { length: 20 }).notNull().default('draft'), // 'draft' | 'planned' | 'published' | 'archived'
});


// --- Audits & Analysis ---

export const seoAuditResultsTable = pgTable('seoAuditResult', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  urlAudited: text('url_audited'),
  results: jsonb('results'),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const websiteCopyAuditsTable = pgTable('websiteCopyAudit', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const pageCopyAuditsTable = pgTable('pageCopyAudit', {
  id: uuid('id').primaryKey().defaultRandom(),
  websiteCopyAuditId: uuid('websiteCopyAuditId').notNull().references(() => websiteCopyAuditsTable.id, { onDelete: 'cascade' }),

  sourceUrl: text('source_url'),
  reasoning: text('reasoning'),
  rewrittenHeadline: text('rewritten_headline'),
  rewrittenBody: text('rewritten_body'),
  rewrittenCta: text('rewritten_cta'),
});


// --- Relationships ---

// --- Prospecting & Enrichment ---

export const prospectsTableRelations = relations(prospectsTable, ({ one, many }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [prospectsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [prospectsTable.organizationId],
    references: [organizationTable.id],
  }),
  generatedEmails: many(generatedEmailsTable),
  sources: many(prospectSourceTable),
  dossier: one(prospectDossierTable, {
    fields: [prospectsTable.id],
    references: [prospectDossierTable.prospectId],
  }),
}));

export const prospectSourceRelations = relations(prospectSourceTable, ({ one }) => ({
  prospect: one(prospectsTable, {
    fields: [prospectSourceTable.prospectId],
    references: [prospectsTable.id],
  }),

  contact: one(contactTable, {
    fields: [prospectSourceTable.contactId],
    references: [contactTable.id],
  }),
}));

// --- Content Generation ---

export const generatedEmailsTableRelations = relations(generatedEmailsTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [generatedEmailsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  prospect: one(prospectsTable, {
    fields: [generatedEmailsTable.prospectId],
    references: [prospectsTable.id],
  }),
  contact: one(contactTable, {
    fields: [generatedEmailsTable.contactId],
    references: [contactTable.id],
  }),
  organization: one(organizationTable, {
    fields: [generatedEmailsTable.organizationId],
    references: [organizationTable.id],
  }),
  enrollment: one(emailSequenceEnrollmentTable, {
    fields: [generatedEmailsTable.enrollmentId],
    references: [emailSequenceEnrollmentTable.id],
  }),
}));

export const blogPlansTableRelations = relations(blogPlansTable, ({ one, many }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [blogPlansTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [blogPlansTable.organizationId],
    references: [organizationTable.id],
  }),
  generatedBlogPosts: many(generatedBlogPostsTable),
}));

export const generatedBlogPostsTableRelations = relations(generatedBlogPostsTable, ({ one }) => ({
  blogPlan: one(blogPlansTable, {
    fields: [generatedBlogPostsTable.blogPlanId],
    references: [blogPlansTable.id],
  }),
}));

export const socialMediaPlansTableRelations = relations(socialMediaPlansTable, ({ one, many }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [socialMediaPlansTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [socialMediaPlansTable.organizationId],
    references: [organizationTable.id],
  }),
  socialMediaPosts: many(socialMediaPostsTable),
}));

export const socialMediaPostsTableRelations = relations(socialMediaPostsTable, ({ one }) => ({
  socialMediaPlan: one(socialMediaPlansTable, {
    fields: [socialMediaPostsTable.planId],
    references: [socialMediaPlansTable.id],
  }),
}));

// --- Audits & Analysis ---

export const seoAuditResultsTableRelations = relations(seoAuditResultsTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [seoAuditResultsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [seoAuditResultsTable.organizationId],
    references: [organizationTable.id],
  }),
}));

export const websiteCopyAuditsTableRelations = relations(websiteCopyAuditsTable, ({ one, many }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [websiteCopyAuditsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [websiteCopyAuditsTable.organizationId],
    references: [organizationTable.id],
  }),
  pageCopyAudits: many(pageCopyAuditsTable),
}));

export const pageCopyAuditsTableRelations = relations(pageCopyAuditsTable, ({ one }) => ({
  websiteCopyAudit: one(websiteCopyAuditsTable, {
    fields: [pageCopyAuditsTable.websiteCopyAuditId],
    references: [websiteCopyAuditsTable.id],
  }),
}));


// ===========================================================================
// EMAIL SENDING INFRASTRUCTURE
// ===========================================================================

/**
 * Stores an organisation's email sending credentials.
 * Supports Resend (API key) and SMTP (host/port/user/encrypted password).
 * SMTP passwords are stored encrypted via Fernet on the Python side;
 * the frontend never reads smtp_password_enc directly.
 */
export const emailProviderTable = pgTable('emailProvider', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  // 'resend' | 'smtp'
  providerType: varchar('provider_type', { length: 20 }).notNull(),
  name: varchar('name', { length: 255 }).notNull(),
  fromEmail: varchar('from_email', { length: 255 }).notNull(),
  fromName: varchar('from_name', { length: 255 }),
  isDefault: boolean('is_default').notNull().default(false),

  // Resend
  resendApiKey: text('resend_api_key'),

  // SMTP
  smtpHost: varchar('smtp_host', { length: 255 }),
  smtpPort: integer('smtp_port'),
  smtpUsername: varchar('smtp_username', { length: 255 }),
  smtpPasswordEnc: bytea('smtp_password_enc'),  // Fernet-encrypted
  smtpUseTls: boolean('smtp_use_tls').notNull().default(true),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_emailProvider_organizationId').on(table.organizationId),
]));


/**
 * Reusable email templates with Jinja2 variable support.
 * body_type: 'html' (React Email / custom HTML) | 'plain' (plain text)
 * Available variables: prospect_name, prospect_company, icebreaker,
 *   sender_name, sender_company, website_url, unsubscribe_link
 */
export const emailTemplateTable = pgTable('emailTemplate', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  name: varchar('name', { length: 255 }).notNull(),
  subjectTemplate: text('subject_template').notNull(),
  // 'html' | 'plain'
  bodyType: varchar('body_type', { length: 10 }).notNull().default('plain'),
  bodyTemplate: text('body_template').notNull(),
  isDefault: boolean('is_default').notNull().default(false),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_emailTemplate_organizationId').on(table.organizationId),
]));


/**
 * A named multi-step outreach sequence (e.g. "3-step cold outreach").
 */
export const emailSequenceTable = pgTable('emailSequence', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_emailSequence_organizationId').on(table.organizationId),
]));


/**
 * One step within a sequence.
 * Either references a template (templateId) or uses inline subject/body overrides.
 * delay_days: days after the previous step to wait before sending (0 = send immediately).
 */
export const emailSequenceStepTable = pgTable('emailSequenceStep', {
  id: uuid('id').primaryKey().defaultRandom(),
  sequenceId: uuid('sequenceId').notNull().references(() => emailSequenceTable.id, { onDelete: 'cascade' }),
  templateId: uuid('templateId').references(() => emailTemplateTable.id, { onDelete: 'set null' }),

  stepNumber: integer('step_number').notNull(),
  delayDays: integer('delay_days').notNull().default(0),

  // Optional overrides — if set, take precedence over the template
  subjectOverride: text('subject_override'),
  bodyOverride: text('body_override'),
}, (table) => ([
  index('IX_emailSequenceStep_sequenceId').on(table.sequenceId),
]));


/**
 * Tracks a prospect's progress through a sequence.
 * status: 'active' | 'paused' | 'completed' | 'replied' | 'unsubscribed'
 * A prospect can only be enrolled once per sequence (unique index).
 */
export const emailSequenceEnrollmentTable = pgTable('emailSequenceEnrollment', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  sequenceId: uuid('sequenceId').notNull().references(() => emailSequenceTable.id, { onDelete: 'cascade' }),
  // One of prospectId or contactId must be set (enforced at app layer)
  prospectId: uuid('prospectId').references(() => prospectsTable.id, { onDelete: 'cascade' }),
  contactId: uuid('contactId').references(() => contactTable.id, { onDelete: 'cascade' }),

  // 'active' | 'paused' | 'completed' | 'replied' | 'unsubscribed'
  status: varchar('status', { length: 20 }).notNull().default('active'),
  currentStep: integer('current_step').notNull().default(1),
  nextSendAt: timestamp('next_send_at', { withTimezone: true }),

  enrolledAt: timestamp('enrolled_at', { withTimezone: true }).defaultNow().notNull(),
  completedAt: timestamp('completed_at', { withTimezone: true }),
}, (table) => ([
  index('IX_enrollment_prospect_sequence').on(table.prospectId, table.sequenceId),
  index('IX_enrollment_contact_sequence').on(table.contactId, table.sequenceId),
  index('IX_enrollment_status_nextSendAt').on(table.status, table.nextSendAt),
]));


// --- Email Infrastructure Relations ---

export const emailProviderTableRelations = relations(emailProviderTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [emailProviderTable.organizationId],
    references: [organizationTable.id],
  }),
}));

export const emailTemplateTableRelations = relations(emailTemplateTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [emailTemplateTable.organizationId],
    references: [organizationTable.id],
  }),
  sequenceSteps: many(emailSequenceStepTable),
}));

export const emailSequenceTableRelations = relations(emailSequenceTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [emailSequenceTable.organizationId],
    references: [organizationTable.id],
  }),
  steps: many(emailSequenceStepTable),
  enrollments: many(emailSequenceEnrollmentTable),
}));

export const emailSequenceStepTableRelations = relations(emailSequenceStepTable, ({ one }) => ({
  sequence: one(emailSequenceTable, {
    fields: [emailSequenceStepTable.sequenceId],
    references: [emailSequenceTable.id],
  }),
  template: one(emailTemplateTable, {
    fields: [emailSequenceStepTable.templateId],
    references: [emailTemplateTable.id],
  }),
}));

export const emailSequenceEnrollmentTableRelations = relations(emailSequenceEnrollmentTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [emailSequenceEnrollmentTable.organizationId],
    references: [organizationTable.id],
  }),
  sequence: one(emailSequenceTable, {
    fields: [emailSequenceEnrollmentTable.sequenceId],
    references: [emailSequenceTable.id],
  }),
  prospect: one(prospectsTable, {
    fields: [emailSequenceEnrollmentTable.prospectId],
    references: [prospectsTable.id],
  }),
  contact: one(contactTable, {
    fields: [emailSequenceEnrollmentTable.contactId],
    references: [contactTable.id],
  }),
  sentEmails: many(generatedEmailsTable),
}));


// --- Infer types for all new tables ---

export type EmailProvider = InferSelectModel<typeof emailProviderTable>;
export type EmailTemplate = InferSelectModel<typeof emailTemplateTable>;
export type EmailSequence = InferSelectModel<typeof emailSequenceTable>;
export type EmailSequenceStep = InferSelectModel<typeof emailSequenceStepTable>;
export type EmailSequenceEnrollment = InferSelectModel<typeof emailSequenceEnrollmentTable>;


export const gmbPlansTable = pgTable('gmbPlan', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),

  optimizationTips: text('optimization_tips').array(),

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const gmbPostsTable = pgTable('gmbPost', {
  id: uuid('id').primaryKey().defaultRandom(),
  gmbPlanId: uuid('gmbPlanId').notNull().references(() => gmbPlansTable.id, { onDelete: 'cascade' }),

  title: text('title').notNull(),
  content: text('content').notNull(),

  feedbackRating: varchar('feedback_rating', { length: 10 }), // 'up' | 'down'
  userFeedback: text('user_feedback'),
  contentStatus: varchar('content_status', { length: 20 }).notNull().default('draft'), // 'draft' | 'planned' | 'published' | 'archived'

  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});


export const gmbPlansTableRelations = relations(gmbPlansTable, ({ one, many }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [gmbPlansTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [gmbPlansTable.organizationId],
    references: [organizationTable.id],
  }),
  gmbPosts: many(gmbPostsTable),
}));

export const gmbPostsTableRelations = relations(gmbPostsTable, ({ one }) => ({
  gmbPlan: one(gmbPlansTable, {
    fields: [gmbPostsTable.gmbPlanId],
    references: [gmbPlansTable.id],
  }),
}));


// =============================================
// --- DataForSEO Integration Tables ---
// =============================================

// --- SERP Rankings (Rank Tracking) ---
export const serpRankingsTable = pgTable('serpRanking', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  keyword: text('keyword').notNull(),
  position: integer('position'),
  previousPosition: integer('previous_position'),
  url: text('url'),
  searchEngine: varchar('search_engine', { length: 20 }).default('google'),
  locationCode: integer('location_code'),
  deviceType: varchar('device_type', { length: 20 }).default('desktop'),
  searchVolume: integer('search_volume'),
  rawResult: jsonb('raw_result'),
  checkedAt: timestamp('checked_at', { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_serpRanking_profile_keyword').on(table.profileId, table.keyword),
]));

export const serpRankingsTableRelations = relations(serpRankingsTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [serpRankingsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [serpRankingsTable.organizationId],
    references: [organizationTable.id],
  }),
}));

// --- Keywords Research ---
export const keywordResearchTable = pgTable('keywordResearch', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  keyword: text('keyword').notNull(),
  source: varchar('source', { length: 30 }).default('search_volume'),
  searchVolume: integer('search_volume'),
  cpc: real('cpc'),
  competition: real('competition'),
  competitionLevel: varchar('competition_level', { length: 20 }),
  monthlySearches: jsonb('monthly_searches').$type<Array<{ year: number; month: number; search_volume: number }>>(),
  rawResult: jsonb('raw_result'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_keywordResearch_profile').on(table.profileId),
]));

export const keywordResearchTableRelations = relations(keywordResearchTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [keywordResearchTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [keywordResearchTable.organizationId],
    references: [organizationTable.id],
  }),
}));

// --- Backlinks ---
export const backlinkProfileTable = pgTable('backlinkProfile', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  targetDomain: text('target_domain').notNull(),
  totalBacklinks: integer('total_backlinks'),
  totalReferringDomains: integer('total_referring_domains'),
  summaryData: jsonb('summary_data'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const backlinkItemTable = pgTable('backlinkItem', {
  id: uuid('id').primaryKey().defaultRandom(),
  backlinkProfileId: uuid('backlinkProfileId').notNull().references(() => backlinkProfileTable.id, { onDelete: 'cascade' }),
  domainFrom: text('domain_from').notNull(),
  urlFrom: text('url_from'),
  urlTo: text('url_to'),
  anchor: text('anchor'),
  domainFromRank: integer('domain_from_rank'),
  isToxic: boolean('is_toxic').default(false),
  rawData: jsonb('raw_data'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const backlinkProfileTableRelations = relations(backlinkProfileTable, ({ one, many }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [backlinkProfileTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [backlinkProfileTable.organizationId],
    references: [organizationTable.id],
  }),
  items: many(backlinkItemTable),
}));

export const backlinkItemTableRelations = relations(backlinkItemTable, ({ one }) => ({
  backlinkProfile: one(backlinkProfileTable, {
    fields: [backlinkItemTable.backlinkProfileId],
    references: [backlinkProfileTable.id],
  }),
}));

// --- On-Page Advanced Audit ---
export const onPageAuditTable = pgTable('onPageAudit', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  targetUrl: text('target_url').notNull(),
  taskId: varchar('task_id', { length: 100 }),
  status: varchar('status', { length: 20 }).notNull().default('pending'),
  crawlProgress: varchar('crawl_progress', { length: 20 }),
  onpageScore: real('onpage_score'),
  summaryData: jsonb('summary_data'),
  pagesData: jsonb('pages_data'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  completedAt: timestamp('completed_at', { withTimezone: true }),
});

export const onPageAuditTableRelations = relations(onPageAuditTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [onPageAuditTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [onPageAuditTable.organizationId],
    references: [organizationTable.id],
  }),
}));

// --- Competitive Analysis ---
export const competitiveAnalysisTable = pgTable('competitiveAnalysis', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  targetDomain: text('target_domain').notNull(),
  competitorDomain: text('competitor_domain'),
  analysisType: varchar('analysis_type', { length: 30 }).notNull(),
  results: jsonb('results'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const competitiveAnalysisTableRelations = relations(competitiveAnalysisTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [competitiveAnalysisTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [competitiveAnalysisTable.organizationId],
    references: [organizationTable.id],
  }),
}));

// --- GMB Business Data ---
export const gmbBusinessDataTable = pgTable('gmbBusinessData', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  businessName: text('business_name'),
  category: text('category'),
  address: text('address'),
  phone: text('phone'),
  rating: real('rating'),
  reviewCount: integer('review_count'),
  rawData: jsonb('raw_data'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const gmbBusinessDataTableRelations = relations(gmbBusinessDataTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [gmbBusinessDataTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [gmbBusinessDataTable.organizationId],
    references: [organizationTable.id],
  }),
}));

// --- AI Mentions / Brand Monitoring ---
export const aiMentionsTable = pgTable('aiMention', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  brandName: text('brand_name').notNull(),
  totalMentions: integer('total_mentions'),
  totalCitations: integer('total_citations'),
  rawResults: jsonb('raw_results'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const aiMentionsTableRelations = relations(aiMentionsTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [aiMentionsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [aiMentionsTable.organizationId],
    references: [organizationTable.id],
  }),
}));

// --- Domain Analytics ---
export const domainAnalyticsTable = pgTable('domainAnalytics', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  domain: text('domain').notNull(),
  registrar: text('registrar'),
  createdDate: timestamp('created_date', { withTimezone: true }),
  expirationDate: timestamp('expiration_date', { withTimezone: true }),
  technologies: jsonb('technologies').$type<Array<{ name: string; category: string; version: string | null }>>(),
  rawWhoisData: jsonb('raw_whois_data'),
  rawTechData: jsonb('raw_tech_data'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
});

export const domainAnalyticsTableRelations = relations(domainAnalyticsTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [domainAnalyticsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [domainAnalyticsTable.organizationId],
    references: [organizationTable.id],
  }),
}));


// --- Google Trends ---
export const googleTrendsTable = pgTable('googleTrends', {
  id: uuid('id').primaryKey().defaultRandom(),
  profileId: uuid('profileId').notNull().references(() => analysisProfileTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId').notNull().references(() => organizationTable.id, { onDelete: 'cascade' }),
  keywords: jsonb('keywords').$type<string[]>().notNull(),
  graphData: jsonb('graph_data'),
  mapData: jsonb('map_data'),
  topicsData: jsonb('topics_data'),
  queriesData: jsonb('queries_data'),
  rawResult: jsonb('raw_result'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_googleTrends_profile').on(table.profileId),
]));

export const googleTrendsTableRelations = relations(googleTrendsTable, ({ one }) => ({
  analysisProfile: one(analysisProfileTable, {
    fields: [googleTrendsTable.profileId],
    references: [analysisProfileTable.id],
  }),
  organization: one(organizationTable, {
    fields: [googleTrendsTable.organizationId],
    references: [organizationTable.id],
  }),
}));


// 1. Define the separate schema namespace
export const companiesHouseSchema = pgSchema('companies_house');

// 2. Define the table
export const companiesHouseTable = companiesHouseSchema.table('companies', {
  // Primary Key
  companyNumber: text('company_number').primaryKey(),
  companyName: text('company_name').notNull(),

  // Address Fields
  regAddressCareOf: text('reg_address_care_of'),
  regAddressPoBox: text('reg_address_po_box'),
  regAddressLine1: text('reg_address_address_line1'),
  regAddressLine2: text('reg_address_address_line2'),
  regAddressPostTown: text('reg_address_post_town'),
  regAddressCounty: text('reg_address_county'),
  regAddressCountry: text('reg_address_country'),
  regAddressPostCode: text('reg_address_post_code'),

  // Core Metadata
  companyCategory: text('company_category'),
  companyStatus: text('company_status'), // e.g., 'Active', 'Dissolved'
  countryOfOrigin: text('country_of_origin'),

  // Dates (We converted these to real Date types in SQL)
  incorporationDate: date('incorporation_date'),
  dissolutionDate: date('dissolution_date'),
  accountsNextDueDate: date('accounts_next_due_date'),
  accountsLastMadeUpDate: date('accounts_last_made_up_date'),

  // Industry Codes (SIC)
  sicCode1: text('sic_code_sic_text_1'),
  sicCode2: text('sic_code_sic_text_2'),
  sicCode3: text('sic_code_sic_text_3'),
  sicCode4: text('sic_code_sic_text_4'),

  // --- KithFlow Enrichment Columns (The "Gold" Layer) ---
  websiteUrl: text('website_url'),
  enrichmentStatus: text('enrichment_status').default('PENDING'), // 'PENDING', 'SEARCHING', 'COMPLETED', 'FAILED'
  aiSummary: text('ai_summary'),
  marketingScore: integer('marketing_score'), // 0-100 score based on data quality
  contactData: jsonb('contact_data'), // Store scraped emails/LinkedIn here

  // Timestamps
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow(),
}, (table) => ([
  // 3. Define the Indexes (These match what we ran in SQL)
  index('idx_ch_postcode').on(table.regAddressPostCode),
  index('idx_ch_status').on(table.companyStatus),
  index('idx_ch_sic').on(table.sicCode1),
  index('idx_ch_enrichment').on(table.enrichmentStatus),
  // Fuzzy search index for name is usually handled by pg_trgm,
  // but Drizzle doesn't need to define it to use it.
]));


// =============================================================================
// Intent Engine — Targeting Rules
// =============================================================================

export type TargetingCriteria = {
  keywords: string;
  location: string;
  sources: string[];
  sic_codes?: string[];
  min_employees?: number;
  max_employees?: number;
};

export const targetingRuleTable = pgTable('targetingRule', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId')
    .notNull()
    .references(() => organizationTable.id, { onDelete: 'cascade' }),
  name: text('name').notNull(),
  clientUrl: text('client_url'),
  criteria: jsonb('criteria').notNull().$type<TargetingCriteria>(),
  sequenceId: uuid('sequenceId').references(() => emailSequenceTable.id, { onDelete: 'set null' }),
  isActive: boolean('is_active').default(true).notNull(),
  runCadence: text('run_cadence').default('daily').notNull(), // 'daily' | 'weekly' | 'manual'
  lastRunAt: timestamp('last_run_at', { withTimezone: true }),
  nextRunAt: timestamp('next_run_at', { withTimezone: true }),
  maxNewProspectsPerRun: integer('max_new_prospects_per_run').default(10).notNull(),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_targetingRule_organizationId').on(table.organizationId),
  index('IX_targetingRule_isActive').on(table.isActive),
]));

export type TargetingRule = InferSelectModel<typeof targetingRuleTable>;

export const targetingRuleTableRelations = relations(targetingRuleTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [targetingRuleTable.organizationId],
    references: [organizationTable.id],
  }),
  sequence: one(emailSequenceTable, {
    fields: [targetingRuleTable.sequenceId],
    references: [emailSequenceTable.id],
  }),
}));


// =============================================================================
// Reply Triage — Inbound Replies
// =============================================================================

export const inboundReplyTable = pgTable('inboundReply', {
  id: uuid('id').primaryKey().defaultRandom(),
  organizationId: uuid('organizationId')
    .notNull()
    .references(() => organizationTable.id, { onDelete: 'cascade' }),
  fromEmail: text('from_email').notNull(),
  fromName: text('from_name'),
  subject: text('subject').notNull(),
  bodyText: text('body_text'),
  bodyHtml: text('body_html'),
  receivedAt: timestamp('received_at', { withTimezone: true }).defaultNow().notNull(),
  // AI classification
  classification: text('classification'),           // 'ooo' | 'unsubscribe' | 'hot_lead' | 'neutral'
  classificationConfidence: real('classification_confidence'),
  classificationReasoning: text('classification_reasoning'),
  // Triage lifecycle
  triageStatus: text('triage_status').default('pending_triage').notNull(), // 'pending_triage' | 'triaged' | 'approved' | 'dismissed'
  // Hot-lead draft reply (Claude Sonnet)
  draftReplySubject: text('draft_reply_subject'),
  draftReplyBody: text('draft_reply_body'),
  // Links to existing records
  enrollmentId: uuid('enrollmentId').references(() => emailSequenceEnrollmentTable.id, { onDelete: 'set null' }),
  prospectId: uuid('prospectId').references(() => prospectsTable.id, { onDelete: 'set null' }),
  rawPayload: jsonb('raw_payload'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_inboundReply_organizationId').on(table.organizationId),
  index('IX_inboundReply_triageStatus').on(table.triageStatus),
  index('IX_inboundReply_classification').on(table.classification),
  index('IX_inboundReply_fromEmail').on(table.fromEmail),
]));

export type InboundReply = InferSelectModel<typeof inboundReplyTable>;

export const inboundReplyTableRelations = relations(inboundReplyTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [inboundReplyTable.organizationId],
    references: [organizationTable.id],
  }),
  enrollment: one(emailSequenceEnrollmentTable, {
    fields: [inboundReplyTable.enrollmentId],
    references: [emailSequenceEnrollmentTable.id],
  }),
  prospect: one(prospectsTable, {
    fields: [inboundReplyTable.prospectId],
    references: [prospectsTable.id],
  }),
}));

// ---------------------------------------------------------------------------
// Prospect Dossier (Battlecard)
// ---------------------------------------------------------------------------

export const prospectDossierTable = pgTable('prospectDossier', {
  id: uuid('id').primaryKey().defaultRandom(),
  prospectId: uuid('prospectId')
    .notNull()
    .unique()
    .references(() => prospectsTable.id, { onDelete: 'cascade' }),
  organizationId: uuid('organizationId')
    .notNull()
    .references(() => organizationTable.id, { onDelete: 'cascade' }),
  generatedAt: timestamp('generated_at', { withTimezone: true }).defaultNow().notNull(),
  status: text('status').default('generating').notNull(), // 'generating' | 'ready' | 'failed'
  // Raw aggregated data
  chData: jsonb('ch_data'),
  websiteContent: text('website_content'),
  pdlData: jsonb('pdl_data'),
  // AI-generated sections (claude-sonnet-4-6, single call)
  executiveSummary: text('executive_summary'),
  painPoints: jsonb('pain_points'),           // string[]
  pitchAngles: jsonb('pitch_angles'),         // string[]
  techStackSignals: jsonb('tech_stack_signals'), // string[]
  recommendedOpening: text('recommended_opening'),
  errorMessage: text('error_message'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ([
  index('IX_prospectDossier_organizationId').on(table.organizationId),
  index('IX_prospectDossier_status').on(table.status),
]));

export type ProspectDossier = InferSelectModel<typeof prospectDossierTable>;

export const prospectDossierTableRelations = relations(prospectDossierTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [prospectDossierTable.organizationId],
    references: [organizationTable.id],
  }),
  prospect: one(prospectsTable, {
    fields: [prospectDossierTable.prospectId],
    references: [prospectsTable.id],
  }),
}));

// ---------------------------------------------------------------------------
// Usage metering ledger
//
// Append-only record of every paid external transaction (AI tokens, scrape
// credits, enrichment/discovery API calls) made by the engine, the chat, or
// background workers — attributed to an organization and (when interactive) a
// user. `costUsd` is the resolved COGS for that single call. `meteredQuantity`
// is the logical allowance unit consumed (1 on the representative row of an
// operation, 0 on its sub-calls), so plan enforcement is a single SUM over the
// billing period. Tiers/allowances live in @workspace/billing's billingConfig;
// nothing here references a plan table.
// ---------------------------------------------------------------------------
export const usageEventTable = pgTable(
  'usageEvent',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    // Nullable: MCP (API key) and Celery (system) transactions have no user.
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    // Set for MCP-originated calls so usage traces back to the key.
    apiKeyId: uuid('apiKeyId').references(() => apiKeyTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    actorType: usageActorTypeEnum('actorType').notNull(),
    surface: varchar('surface', { length: 16 }).notNull(), // engine | chat | mcp | worker
    provider: usageProviderEnum('provider').notNull(),
    operation: varchar('operation', { length: 64 }).notNull(),
    meter: usageMeterEnum('meter'),
    model: varchar('model', { length: 64 }),
    quantity: integer('quantity').default(1).notNull(), // raw units: calls / pages
    inputTokens: integer('inputTokens'),
    outputTokens: integer('outputTokens'),
    costUsd: doublePrecision('costUsd').default(0).notNull(), // resolved COGS
    meteredQuantity: integer('meteredQuantity').default(0).notNull(),
    isOverage: boolean('isOverage').default(false).notNull(),
    billable: boolean('billable').default(true).notNull(),
    referenceType: varchar('referenceType', { length: 32 }),
    referenceId: uuid('referenceId'),
    metadata: jsonb('metadata'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    index('IX_usageEvent_organizationId_createdAt').on(
      table.organizationId,
      table.createdAt
    ),
    index('IX_usageEvent_organizationId_meter_createdAt').on(
      table.organizationId,
      table.meter,
      table.createdAt
    ),
    index('IX_usageEvent_userId').on(table.userId)
  ]
);

export type UsageEvent = InferSelectModel<typeof usageEventTable>;

export const usageEventRelations = relations(usageEventTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [usageEventTable.organizationId],
    references: [organizationTable.id]
  }),
  user: one(userTable, {
    fields: [usageEventTable.userId],
    references: [userTable.id]
  }),
  apiKey: one(apiKeyTable, {
    fields: [usageEventTable.apiKeyId],
    references: [apiKeyTable.id]
  })
}));